/** The recommendation board: ranked plans whose actions follow the server's clock (U026-U030). */
import { decodeBoardRow, type BoardRow } from "../../contracts/views.ts";
import {
  badge,
  definitions,
  instantCell,
  metric,
  renderFreshnessBar,
} from "../../app/components.ts";
import { h, setChildren } from "../../app/dom.ts";
import { formatPaise, formatQuantity } from "../../app/format.ts";
import { hashOf } from "../../app/router.ts";
import { renderStateBanner, stateOfEnvelope, stateOfError } from "../../app/state.ts";
import { setCommandEnabled } from "../../app/command-dialog.ts";
import { anchor, isAged, type ServerClock } from "../../app/time.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { handleOf, makeFrame } from "../frame.ts";
import { byRank, evaluatePlan, probabilityView, safeHref, type PlanView } from "./model.ts";

function planDetails(row: BoardRow): HTMLElement | null {
  const { plan } = row;
  if (plan === null) {
    return null;
  }
  return h(
    "details",
    { class: "plan" },
    h("summary", {}, `Plan for ${row.listing}`),
    definitions([
      ["Trigger", formatPaise(plan.triggerPaise)],
      [
        "Allowed entry range",
        `${formatPaise(plan.entryLowPaise)} to ${formatPaise(plan.entryHighPaise)}`,
      ],
      ["Initial stop", formatPaise(plan.stopPaise)],
      ["Quantity", formatQuantity(plan.quantity)],
      ["Notional", formatPaise(plan.notionalPaise)],
      ["Planned risk", formatPaise(plan.plannedRiskPaise)],
      ["Stressed risk", formatPaise(plan.stressedRiskPaise)],
      ["Estimated costs", formatPaise(plan.costsPaise)],
      ["Exit policy", plan.exitPolicy],
      ["Expires", instantCell(row.expiresAt)],
    ]),
  );
}

interface Entry {
  readonly view: PlanView;
  readonly card: HTMLElement;
  readonly button: HTMLButtonElement;
  readonly countdown: HTMLElement;
}

const SAVED_NOTE = "Saved locally. No order was placed and nothing was sent.";

function leftText(seconds: number): string {
  return `${String(seconds)} s left (server time)`;
}

/** Mount the recommendation board. */
export function mountBoard(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("board", "Recommendation board");
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  let rows: BoardRow[] = [];
  let clock: ServerClock | null = null;
  let actionsEnabled = false;
  let ageUs: number | null = null;
  let boundUs = 0;
  /** The envelope allowed actions and its data has not since outgrown its freshness bound (F1). */
  const enabledAt = (current: ServerClock): boolean =>
    actionsEnabled && !isAged(ageUs, boundUs, current, ctx.mono);
  const saved = new Set<string>();
  let entries = new Map<string, Entry>();

  /** Re-derive each control's enabled state in place; a focused control that turns off hands focus to its card. */
  const syncEnabled = (): void => {
    for (const { view, button, card } of entries.values()) {
      const on = view.actionable && ctx.commandsEnabled.get();
      const wasFocused = document.activeElement === button;
      setCommandEnabled(button, on);
      if (wasFocused && !on) {
        card.focus();
      }
    }
  };

  const plan = (view: PlanView): Entry => {
    const { row } = view;
    const probability = probabilityView(row);
    const link = row.catalyst === null ? null : safeHref(row.catalyst.sourceUrl);
    const note = h(
      "p",
      { class: "note", attrs: { "aria-live": "polite" } },
      saved.has(row.planId) ? SAVED_NOTE : "",
    );
    const button = h(
      "button",
      {
        class: "save-plan",
        attrs: {
          type: "button",
          "data-command-control": "save-plan",
          "aria-label": `Save a note for the ${row.listing} plan (no order is placed)`,
        },
        on: {
          click: () => {
            saved.add(row.planId);
            note.textContent = SAVED_NOTE;
          },
        },
      },
      "Save plan note",
    );
    const countdown = h(
      "span",
      { class: "countdown" },
      view.actionable ? leftText(view.secondsLeft) : "not actionable",
    );
    const card = h(
      "li",
      {
        class: `plan-card plan-${row.status.toLowerCase()}`,
        attrs: {
          "data-plan": row.planId,
          "data-actionable": String(view.actionable),
          tabindex: "-1",
        },
      },
      h(
        "h2",
        {},
        `#${String(row.rank)} ${row.listing} ${row.side} `,
        badge(row.status, row.status === "ACTIVE" ? "ok" : "bad"),
      ),
      definitions([
        ["Setup", row.setup],
        ["Evidence status", row.evidenceStatus],
        [
          "Probability",
          h(
            "span",
            { class: "probability", attrs: { "data-calibration": row.calibrationStatus } },
            metric(probability.text),
            ` (${probability.note})`,
          ),
        ],
        ["Decision time", instantCell(row.decisionAt)],
        ["Allocation", row.allocation],
        [
          "Catalyst",
          row.catalyst === null
            ? metric(null)
            : link === null
              ? `${row.catalyst.label} (revision ${String(row.catalyst.revision)}; source link withheld)`
              : h(
                  "a",
                  { attrs: { href: link, rel: "noopener noreferrer nofollow", target: "_blank" } },
                  `${row.catalyst.label} (revision ${String(row.catalyst.revision)})`,
                ),
        ],
        ["Expiry", countdown],
      ]),
      view.actionable
        ? null
        : h(
            "p",
            { class: "blocked", attrs: { "data-blocked": "true" } },
            `Not actionable: ${view.blockedBy.join("; ")}.`,
          ),
      planDetails(row),
      h("p", { class: "why" }, `Why: ${row.explanation}`),
      h(
        "a",
        { attrs: { href: hashOf("opportunities", "detail", row.decisionId) } },
        `Inspect ${row.listing} decision`,
      ),
      button,
      note,
    );
    return { view, card, button, countdown };
  };

  const draw = (current: ServerClock): void => {
    const focused = frame.body.querySelector<HTMLElement>("[data-plan]:focus-within")?.dataset.plan;
    entries = new Map(
      byRank(rows).map((row) => [
        row.planId,
        plan(evaluatePlan(row, current, ctx.mono, enabledAt(current))),
      ]),
    );
    setChildren(frame.body, [
      h(
        "ol",
        { class: "board", attrs: { "aria-label": "Ranked candidates" } },
        ...[...entries.values()].map((entry) => entry.card),
      ),
    ]);
    syncEnabled();
    if (focused !== undefined) {
      entries.get(focused)?.card.focus();
    }
  };

  /** Once a second: update countdowns, and redraw when a plan's actionability changed (expiry). */
  const tick = (): void => {
    if (clock === null) {
      return;
    }
    const current = clock;
    const enabled = enabledAt(current);
    const changed = rows.some(
      (row) =>
        entries.get(row.planId)?.view.actionable !==
        evaluatePlan(row, current, ctx.mono, enabled).actionable,
    );
    if (changed) {
      draw(current);
      return;
    }
    for (const { view, countdown } of entries.values()) {
      if (view.actionable) {
        countdown.textContent = leftText(
          evaluatePlan(view.row, current, ctx.mono, enabled).secondsLeft,
        );
      }
    }
  };
  ctx.scope.every(1000, tick);
  ctx.scope.add(ctx.commandsEnabled.subscribe(syncEnabled));

  void ctx.client
    .read("board", decodeBoardRow, { accountId: ctx.accountId })
    .then((answer) => {
      if (answer === null || ctx.scope.disposed) {
        return;
      }
      rows = [...answer.page.rows];
      clock = anchor(answer.snapshotTime, ctx.mono);
      actionsEnabled = answer.actionsEnabled;
      ageUs = answer.ageUs;
      boundUs = answer.freshnessBoundUs;
      const state = stateOfEnvelope(answer);
      setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
      setChildren(frame.freshness, [
        renderFreshnessBar(answer, { streamSequence: ctx.streamSequence.get() }),
      ]);
      draw(clock);
    })
    .catch((error: unknown) => {
      setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
    });
  return handleOf(frame, ctx);
}
